import { Icon } from "@mdi/react";
import { FC, KeyboardEvent as ReactKeyboardEvent, ReactNode, useState } from "react";
import { toast } from "react-toastify";

import { mdiCalendarClock, mdiDelete, mdiPencil, mdiPlus } from "@mdi/js";
import { Button, Input, List, Popconfirm, Popover, Tooltip, Typography } from "antd";
import styled from "styled-components";

import { IconButton } from "components/forecasting/Shared";

import useDeleteAutomationCollection from "../hooks/useDeleteAutomationCollection";
import useFetchAutomationSchedules from "../hooks/useFetchAutomationSchedules";
import usePostAutomationCollection from "../hooks/usePostAutomationCollection";
import useUpdateAutomationCollection from "../hooks/useUpdateAutomationCollection";
import { AutomationCollection } from "../types/automation";
import { AutomationScheduleModal } from "./AutomationScheduleModal";

const ScheduleIndicator = styled.div`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 5;
`;

const Container = styled.div`
  background: white;
  width: 100%;
  height: 100%;
  max-width: 250px;
  width: 250px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e5e5;
`;

const HeaderContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AutomationList = styled(List)`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const ItemWrapper = styled(List.Item)`
  cursor: pointer;
  min-width: 120px;
  height: 40px;
  padding: 0 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ selected }) => (selected ? "#f0f0f0" : "white")};
  transition: box-shadow 0.3s ease-in-out;
  z-index: 1;
  overflow: hidden;
  position: relative;

  &:hover {
    box-shadow: 0 0 10px rgba(159, 169, 179, 0.5);
    z-index: 10; /* Ensure the hovered item is above others */
  }
`;

const ActionsContainer = styled.span<{ isEditing: boolean }>`
  display: flex;
  gap: 4px;
  margin-left: 8px;
  opacity: ${({ isEditing }) => (isEditing ? 1 : 0)};
  visibility: ${({ isEditing }) => (isEditing ? "visible" : "hidden")};
  transition: opacity 0.2s ease, visibility 0.2s ease;

  ${ItemWrapper}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const OkButton = styled(Button)`
  padding: 0;
  margin-right: 4px;
`;

const ActionButton = styled(IconButton)``;

const NameInput = styled(Input)`
  width: 160px;
  margin-right: 4px;
`;

const ItemName = styled.span<{ hasSchedule?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  padding-left: ${(props) => (props.hasSchedule ? "24px" : "0")};
`;

const AddButtonPopoverContent = styled.div`
  min-width: 200px;
`;

const AddButtonContainer = styled.div`
  margin-bottom: 8px;
`;

interface AddAutomationButtonProps {
  onAdd: () => void;
  disabled: boolean;
  children: ReactNode;
}

const AddAutomationButton: FC<AddAutomationButtonProps> = ({
  onAdd,
  disabled,
  children
}) => (
  <Button type="primary" size="small" block onClick={onAdd} disabled={disabled}>
    {children}
  </Button>
);

interface AutomationListPanelProps {
  automations: AutomationCollection[];
  selectedAutomation: AutomationCollection;
  onSelectedAutomation: (command: AutomationCollection) => void;
  onAutomationChange: (automations: AutomationCollection[]) => void;
}

const AutomationListPanel: FC<AutomationListPanelProps> = ({
  automations,
  selectedAutomation,
  onSelectedAutomation
}: AutomationListPanelProps) => {
  const { mutateAsync: deleteAutomation } = useDeleteAutomationCollection();
  const { mutateAsync: createAutomation } = usePostAutomationCollection();
  const { mutateAsync: updateAutomation } = useUpdateAutomationCollection();
  const { data: automationSchedules = [] } = useFetchAutomationSchedules();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState<AutomationCollection | null>(null);

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) {
      toast.error("Collection name cannot be empty");
      return;
    }
    try {
      const newAutomation: AutomationCollection = {
        name: newCollectionName.trim(),
        _id: null,
        groups: []
      };
      await createAutomation(newAutomation);
      setNewCollectionName("");
      setPopoverVisible(false);
    } catch (err) {
      toast.error("Failed to create automation");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAutomation(id);
    } catch (err) {
      toast.error("Error occurred deleting.");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleRename = async (id: string) => {
    if (!editingName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }

    try {
      await updateAutomation({ id, name: editingName.trim() });
      toast.success("Automation renamed successfully");
      setEditingId(null);
      setEditingName("");
    } catch (err) {
      toast.error("Error occurred updating automation");
    }
  };

  const handleSchedule = (automation: AutomationCollection) => {
    setScheduleTarget(automation);
    setScheduleModalOpen(true);
  };

  const handleScheduleCancel = () => {
    setScheduleModalOpen(false);
    setScheduleTarget(null);
  };

  // Check if an automation has an active schedule
  const hasSchedule = (automationId: string): boolean => {
    return automationSchedules.some((schedule) => schedule.automationId === automationId);
  };

  return (
    <>
      <Container>
        <HeaderContainer>
          <Typography.Text strong>Automations</Typography.Text>
          <Popover
            trigger="click"
            content={
              <AddButtonPopoverContent>
                <AddButtonContainer>
                  <Input
                    placeholder="Enter collection name"
                    size="small"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    onPressEnter={handleCreateCollection}
                  />
                </AddButtonContainer>
                <AddAutomationButton
                  onAdd={handleCreateCollection}
                  disabled={!newCollectionName?.trim()}>
                  OK
                </AddAutomationButton>
              </AddButtonPopoverContent>
            }
            visible={popoverVisible}
            onVisibleChange={setPopoverVisible}>
            <IconButton icon={<Icon path={mdiPlus} size={1.2} />} size="small" />
          </Popover>
        </HeaderContainer>
        <AutomationList
          dataSource={automations}
          renderItem={(item: AutomationCollection) => (
            <ItemWrapper
              selected={item == selectedAutomation}
              onClick={() => onSelectedAutomation(item)}>
              {editingId === item._id ? (
                <NameInput
                  size="small"
                  value={editingName}
                  autoFocus
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Escape") {
                      handleCancelEdit();
                      e.preventDefault();
                    } else if (e.key === "Enter") {
                      handleRename(item._id);
                    }
                  }}
                />
              ) : (
                <>
                  {hasSchedule(item._id) && (
                    <ScheduleIndicator
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSchedule(item);
                      }}>
                      <Tooltip title="View/edit schedule">
                        <Icon path={mdiCalendarClock} size={0.8} color="#1890ff" />
                      </Tooltip>
                    </ScheduleIndicator>
                  )}
                  <ItemName hasSchedule={hasSchedule(item._id)}>{item.name}</ItemName>
                </>
              )}
              <ActionsContainer
                isEditing={editingId === item._id}
                onClick={(e) => e.preventDefault()}>
                {editingId === item._id ? (
                  <OkButton
                    size="small"
                    type="link"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await handleRename(item._id);
                    }}
                    disabled={!editingName.trim()}>
                    OK
                  </OkButton>
                ) : (
                  <>
                    {!hasSchedule(item._id) && (
                      <Tooltip title="Schedule automation">
                        <ActionButton
                          className="schedule-button"
                          icon={<Icon path={mdiCalendarClock} size={1} />}
                          size="small"
                          type="text"
                          onClick={() => handleSchedule(item)}
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="Rename automation">
                      <ActionButton
                        className="edit-button"
                        icon={<Icon path={mdiPencil} size={1} />}
                        size="small"
                        type="text"
                        onClick={() => {
                          setEditingId(item._id);
                          setEditingName(item.name);
                        }}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Delete this automation?"
                      onConfirm={() => handleDelete(item._id)}
                      okText="Yes"
                      cancelText="No">
                      <Tooltip title="Delete automation">
                        <IconButton
                          danger
                          className="delete-button"
                          icon={<Icon path={mdiDelete} size={1} />}
                          size="small"
                          type="text"
                        />
                      </Tooltip>
                    </Popconfirm>
                  </>
                )}
              </ActionsContainer>
            </ItemWrapper>
          )}
        />
      </Container>
      <AutomationScheduleModal
        open={scheduleModalOpen}
        automation={scheduleTarget}
        onCancel={handleScheduleCancel}
      />
    </>
  );
};

export default AutomationListPanel;
